import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Alert,
    Box,
    Button,
    Typography,
} from '@mui/material';
import { AttendeeDto, AttendeeListDto } from '../../../Component/Model/Dto/WSP/Attendees/MerSETAAttendees';
import { MerSETAExternalAttendeeService } from '../../../Component/Api/Attendee/MerSETAExternalAttendeeService';
import AppSelectListWithInfoAndSearch from '../../../Component/Common/Components/FormValidationInputs/AppSelectListWithInfoAndSearch';
import { useForm } from 'react-hook-form';
import '../../../Component/Common/Components/DataTable/DataTable.scss';
import { toast } from 'react-toastify';
import { ConfirmationDialog } from '../../../Component/Common/Components/ConfirmationDialog';
import { defaultObject } from '../../../Helpers/typescriptHelper';
import { AttendeeLookup } from '../../../Component/Model/Dto/WSP/Attendees/AttendeeLookup';
import { MerSETASDEService } from '../../../Component/Api/merSETA/External/MerSETASDEService';
import { StipendsModal } from '../PeopleAndTraining/Training/External/StipendsModal';
import { useRecoilValue } from 'recoil';
import { Link } from 'react-router-dom';
import { InterventionAtom } from '../PeopleAndTraining/Training/Intervention.Atom';
import { DataTable } from '../../../Component/Common/Components/DataTable/DataTable';
import { AttendeeTableMapper } from './AttendeeTableMapper';
import { IntermediateGlobalLoading } from '../../../Component/Common/Components/Loading/IntermediateGlobalLoading';
import { isNullOrEmpty } from '../../../Component/Model/helpers';
import { EntriesPerPage } from '../../../Component/Model/Table/Table';
import { getDateWithoutTime } from '../../../Helpers/DateFormatter';

interface IProps {
    organisationId: number;
    organisationName: string;
}

interface SelectedAttendee {
    name: string;
    platformPeopleId: string;
}

export const MersetaExternalAttendees = (props: IProps) => {
    const [data, setData] = useState<AttendeeDto[]>([]);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageIndex, setPageIndex] = useState<number>(0);
    const pageSize: number = 10;
    const [selectedAttendee, setSelectedAttendee] = useState<string[]>([]);
    const [attendees, setAttendees] = useState<AttendeeLookup[]>([]);
    const [showDeleteConfirmDialog, setShowConfirmDeleteDialog] = useState<boolean>(false);
    const [selectedAttendeeToDelete, setSelectedAttendeeToDelete] = useState<SelectedAttendee>(defaultObject);
    const { control, formState: { isSubmitting } } = useForm({
        mode: 'all',
    });
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [searchValue, setSearchValue] = useState<string|null>(null);
    const [beeCategory, setBeeCategory] = useState<number>(0);
    const [startDate, setStartDate] = useState<Date>(getDateWithoutTime(new Date()));
    const [endDate, setEndDate] = useState<Date>(getDateWithoutTime(new Date()));
    const [selectedAttendeeForStipend, setSelectedAttendeeForStipend] = useState<SelectedAttendee>(defaultObject);
    const [showStipendsModal, setShowStipendsModal] = useState<boolean>(false);
    const [isHEBursary, setIsHEBursary] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [filteredItemCount, setFilteredItemCount] = useState<number>(0);
    const [clearDropdownKey, setClearDropdownKey] = useState<number>(0);
    const [tableRefreshKey, setTableRefreshKey] = useState<number>(0);


    const selectedSDE = useRecoilValue(InterventionAtom);
    const changeSelectedAttendeeToAdd = (value: string[]) => {
        setSelectedAttendee(value);
    }
    useEffect(() => {
        if (selectedSDE.id > -1) {
            fetchData();
        }
    }, [entriesPerPage, pageNumber, searchValue, tableRefreshKey, selectedSDE]);

    const handleDeleteAttendee = async () => {
        await MerSETAExternalAttendeeService.deleteAttendee(selectedAttendeeToDelete?.platformPeopleId, selectedSDE.id);
        toast.success(`${selectedAttendeeToDelete.name} has been deleted`);
        setShowConfirmDeleteDialog(false);
        fetchData();
    }


    const showDeleteAttendeeConfirmation = async (id: string, name: string) => {
        setSelectedAttendeeToDelete({ platformPeopleId: id, name: name });
        setShowConfirmDeleteDialog(true);
    }

    const handleStipendForAttendee = async (id: string, name: string) => {
        setSelectedAttendeeForStipend({ platformPeopleId: id, name: name });
        setShowStipendsModal(true);
    }

    const addAttendee = async () => {
        let attendeeList: AttendeeListDto = { interventionId: selectedSDE.id, platformPeopleIdList: selectedAttendee };
        if (selectedAttendee.length === 0) {
            toast.error('No attendee has been selected to be added');
            return;
        }

        try {
            await MerSETAExternalAttendeeService.addAttendee(attendeeList).then((newAttendeeAdded) => {
                toast.success(attendeeList.platformPeopleIdList.length + (attendeeList.platformPeopleIdList.length > 1 ? ' Attendees' : ' Attendee') + ' have been added.');
                setSelectedAttendee([]);
                fetchData();
            });
        } catch (error) {
            console.log(error);
        }
    }


    const fetchData = async () => {
        try {
            setIsLoading(true);
            await MerSETASDEService.GetById(selectedSDE.id).then(async (merSetaSDE) => {
                setBeeCategory(merSetaSDE.data.merSetaBEECategoryId);
                setIsHEBursary(merSetaSDE.data.merSetaBEECategoryId === 2);
                setStartDate(merSetaSDE.data.startDate);
                setEndDate(merSetaSDE.data.endDate);

                let result = await MerSETAExternalAttendeeService.getAllocatedAttendees(props.organisationId, selectedSDE.id, pageNumber, entriesPerPage.defaultValue, searchValue ?? '');
                setData(result.data.results);
                setTotalItemsCount(result.data.totalItemsCount);
                setFilteredItemCount(result.data.filteredItemsCount);

                let attendeeResult = await MerSETAExternalAttendeeService.getAttendees(props.organisationId, selectedSDE.id);
                setAttendees(attendeeResult.data);
            })


        } catch (error) {
            console.log(error);
            setTotalItemsCount(1);
        } finally {
            setIsLoading(false);

        }
    };

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    };

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    };

    const handleCloseStipendsModal = () => {
        setShowStipendsModal(false);
        setPageIndex(0);
        setTableRefreshKey(prevKey => prevKey + 1);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    }

    const showNoAttendeesWarning = !isLoading && attendees?.length === 0 && data?.length === 0 && selectedSDE.id > -1 && isNullOrEmpty(searchValue);
    const attendeeTableData = AttendeeTableMapper(data, showDeleteAttendeeConfirmation, handleStipendForAttendee, isHEBursary);
    if (isLoading && searchValue === null) {
        return <IntermediateGlobalLoading />
    }
    else {
        return (
            <>

                <div>
                    {isHEBursary ?
                        <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '30px' }}>
                            <thead>
                                <tr>
                                    <th style={{ backgroundColor: '#E9ECEF', fontWeight: 'bold', padding: '10px', textAlign: 'left', fontSize: '17px', height: '15px' }}>STEPS TO COMPLETE</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td
                                        style={{
                                            position: 'relative',
                                            padding: '10px',
                                            borderBottom: '0.5px solid #ccc',
                                            fontSize: '15px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: '0',
                                                top: '0',
                                                width: '8px',
                                                height: '100%',
                                                backgroundColor: '#2E7D32',
                                                paddingRight: '5px',
                                            }}
                                        ></div>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <div
                                                style={{
                                                    backgroundColor: '#2E7D32',
                                                    color: '#fff',
                                                    borderRadius: '50%',
                                                    width: '30px',
                                                    height: '30px',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    marginLeft: '5px',
                                                    marginRight: '5px',
                                                }}
                                            >
                                                1
                                            </div>
                                            You may bulk add attendees to this training intervention.
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        style={{
                                            position: 'relative',
                                            padding: '10px',
                                            borderBottom: '0.5px solid #ccc',
                                            fontSize: '15px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: '0',
                                                top: '0',
                                                width: '8px',
                                                height: '100%',
                                                backgroundColor: '#1C88D1',
                                                paddingRight: '5px',
                                            }}
                                        ></div>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <div
                                                style={{
                                                    backgroundColor: '#1C88D1',
                                                    color: '#fff',
                                                    borderRadius: '50%',
                                                    width: '30px',
                                                    height: '30px',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    marginLeft: '5px',
                                                    marginRight: '5px',
                                                }}
                                            >
                                                2
                                            </div>
                                            Once attendees are added to the table, you may add a stipend for each attendee individually.
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        :
                        <div style={{ paddingBottom: '20px' }}>
                            <Typography variant="body2">
                                You may bulk add attendees to this training intervention.
                            </Typography>
                        </div>
                    }

                    {showNoAttendeesWarning ? (
                        <Box pb={3} sx={{ width: '100%' }}>
                            <Alert severity="warning" style={{ paddingTop: 5 }}>
                                There are no available people to add to this training intervention. Please capture people for this organisation.
                            </Alert>
                            <Box pt={1}>
                                <Link to={`/organisation/people`}>
                                    <Button color="primary" variant="contained" >
                                        Manage People
                                    </Button>
                                </Link>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            {(attendees?.length > 0 && !isLoading) ? (
                                <Box pb={3} sx={{ width: '40%' }}>
                                    <AppSelectListWithInfoAndSearch
                                        key={clearDropdownKey}
                                        control={control}
                                        name="attendee"
                                        label="Select person/people within your organisation"
                                        size="small"
                                        options={attendees}
                                        onChange={(value: string[]) => changeSelectedAttendeeToAdd(value)}
                                        disableEntries={true}
                                        onAdd={() => addAttendee()}
                                    />
                                </Box>
                            ) : (
                                <Box pb={3} sx={{ width: '100%' }}>
                                    <Alert severity="warning" style={{ paddingTop: 5 }}>
                                        All available people have been added to this intervention.
                                    </Alert>
                                </Box>
                            )}


                            <DataTable
                                key={tableRefreshKey}
                                table={attendeeTableData}
                                entriesPerPage={entriesPerPage}
                                pageNumber={pageNumber}
                                filteredItemsCount={filteredItemCount}
                                totalItemsCount={totalItemsCount}
                                isLoading={isLoading}
                                searchValueChanged={searchValueChanged}
                                canSearch={true}
                                entriesPerPageChanged={entriesPerPageChanged}
                                pageNumberChanged={pageNumberChanged} />

                            <ConfirmationDialog
                                showDialog={showDeleteConfirmDialog}
                                handleAccept={handleDeleteAttendee}
                                handleCancel={() => { setShowConfirmDeleteDialog(false) }}
                                heading={"Delete Attendee"}
                                message={`Are you sure you want to remove ${selectedAttendeeToDelete.name} from this training intervention?`}
                            />

                            {showStipendsModal &&
                                <StipendsModal
                                    attendee={selectedAttendeeForStipend}
                                    merSetaId={selectedSDE.id}
                                    startDate={startDate}
                                    endDate={endDate}
                                    handleClose={handleCloseStipendsModal}
                                    showModal={showStipendsModal}
                                />
                            }
                        </>
                    )}
                </div>
            </>
        )
    };
}
//}

